import React, { useEffect, useState } from "react";
import Seo from "../../../Components/SeoComponent/Seo";
import Header from "../../../Components/Header";
import { useService } from "../../../context/ServiceContext";
import { Copy, FileJson, FormInput, ListCheck, Loader } from "lucide-react";
import { verifyRcCardDetails } from "../../../Services/ApiTestService/apiTestService";
import { fetchProfile } from "../../../Services/ApiTestService/aadharTestService";
import { showErrorToast, showSuccessToast } from "../../../utils/toastUtils";
import { isNumeric, isAlpha, isAlphanumeric } from "validator";

function RcCardDetails() {
  const { serviceName } = useService();

  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState(null);

  const [regNumber, setRegNumber] = useState("");
  const [inputError, setInputError] = useState(null);
  const [isJsonCopied, setIsJsonCopied] = useState(false);

  const [profileData, setProfileData] = useState(null);
  let apiResponse;

  const isAlphaNumeric = (value) => {
    if (!value) return false;

    return isAlphanumeric(value, "en-IN", { ignore: " " });
  };

  const handleCopy = (data) => {
    navigator.clipboard
      .writeText(JSON.stringify(data, null, 2))
      .then(setIsJsonCopied(true));
    showSuccessToast("Text Copied");
  };

  const handleRcCard = async () => {
    if (!profileData) return;

    const validateRegNo = isAlphaNumeric(regNumber);

    if (!validateRegNo) {
      return setInputError(
        "⚠️ please enter Alpha Numeric chars in Reg.No: field",
      );
    } else if (regNumber.length < 9) {
      return setInputError("⚠️ enter at least 9 digits in Reg No. field");
    }

    try {
      setInputError(null);
      setIsLoading(true);
      setResponse(null);

      const res = await verifyRcCardDetails({
        api_id: profileData.user_id,
        api_key: profileData.api_key,
        token_id: profileData.token_id,
        reg_number: regNumber.trim(),
      });

      if (res) {
        apiResponse = res;

      }
      setResponse(apiResponse);

      if (res?.status?.code === 200) {
        showSuccessToast(res?.message || "Success");
      } else if (res?.status?.code === 422) {
        showErrorToast(res?.data?.message || "invalid details ");
      } else if (res?.status?.code === 500) {
        showErrorToast(res?.data?.message || "server error");
      } else if (!res.success) {
        showErrorToast(res?.data?.message || "verificaton failed");
      } else {
        showErrorToast(res?.data?.message || "something went wrong");
      }
    } catch (error) {
      console.error(error);
      showErrorToast("Server error");
    } finally {
      setIsLoading(false);
    }
  };

  // ---------------- FETCH PROFILE ----------------
  useEffect(() => {
    const loadProfile = async () => {
      try {
        setIsLoading(true);
        const res = await fetchProfile();
        setProfileData(res?.data || null);
      } catch (error) {
        console.error(error);
        showErrorToast("Failed to fetch profile");
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, []);

  return (
    <>
      <Seo />
      <Header heading={serviceName} />

      {isLoading && (
        <div className="flex justify-center mt-6">
          <Loader className="animate-spin" />
        </div>
      )}

      <div className="w-full min-h-screen flex flex-col items-center justify-start py-14">
        {/* ---------------- FORM ---------------- */}
        <div className=" max-w-3xl border rounded-md p-4 sm:p-6 mt-2 sm:mt-6 bg-white">
          <div className="flex flex-col gap-4">
            {/* Mobile */}
            <div className=" flex flex-wrap items-center gap-4">
              <label className="w-32 flex text-nowrap items-center font-bold">
                Registration No <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                maxLength={15}
                value={regNumber}
                onChange={(e) => setRegNumber(e.target.value)}
                className="flex-1 border border-gray-400 rounded-md px-3 h-[38px]"
                placeholder="Eg: 8787466545"
              />
            </div>

            {/* show input errors */}

            {inputError && (
              <div className="text-red-800 text-xl font-bold">{inputError}</div>
            )}

            {/* Verify Button */}
            <div className="flex justify-end mt-2">
              <button
                disabled={isLoading || !regNumber}
                onClick={handleRcCard}
                className="px-6 py-2 bg-gradient-to-br hover:bg-black active:bg-green-600 from-blue-400 to-blue-800 text-white rounded-sm disabled:opacity-50"
              >
                Verify
              </button>
            </div>
          </div>
        </div>

        {response && (
          <div
            className={` ${response.status?.code === 500 || response.status?.code === 422 ? "text-red-500" : "text-blue-500"} py-4 text-2xl font-bold`}
          >
            {response?.data?.message || response?.message}
          </div>
        )}

        {/* instructions */}

        <div>
          <div className="bg-gray-200 border border-gray-400 p-5 mt-12 rounded-md">
            <h3 className="font-bold text-lg mb-2">
              ‼️ Important Instructions <span className="text-red-600">*</span>
            </h3>

            <p className="text-sm leading-relaxed">
              Please enter a valid Registration Number. clicking the Verify
              button. After successful verification, the system will return the
              response in structured format, including contact details
              associated with the provided details. You may review and copy the
              data for your reference or further use.
            </p>
          </div>
        </div>

        {/* ---------------- RESULT ---------------- */}
        {response?.status?.code === 200 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10 w-full max-w-6xl">
            {/* -------- JSON VIEW -------- */}
            <div className="bg-gray-900 text-green-300 p-4 relative rounded-md overflow-auto">
              <button
                onClick={() => handleCopy(response)}
                className="absolute text-white top-4 right-8"
              >
                {isJsonCopied ? "copied" : <Copy />}
              </button>

              <p className="font-bold mb-2 text-2xl flex items-center justify-start md:justify-center gap-2 text-white">
                <FileJson /> JSON response
              </p>
              <hr className="mb-8" />

              <pre className="text-[16px]">
                {JSON.stringify(response, null, 2)}
              </pre>
            </div>

            {/* -------- USER DETAILS -------- */}
            <div className="bg-blue-500 p-4 border-2 border-blue-500 rounded-md">
              <h2 className="text-2xl flex items-center justify-center gap-2  font-bold text-white mb-1">
                <ListCheck /> All Details
              </h2>
              <hr className="mb-8" />

              <div className="space-y-2 text-sm">
                <div>
                  <span className="font-semibold text-blue-200 w-56 inline-block">
                    State:
                  </span>
                  <span className="text-white">
                    {response?.data?.state || "N/A"}
                  </span>
                </div>

                <div>
                  <span className="font-semibold text-blue-200 w-56 inline-block">
                    Vehicle Class:
                  </span>
                  <span className="text-white">
                    {response?.data?.vehicleClass || "N/A"}
                  </span>
                </div>

                <div>
                  <span className="font-semibold text-blue-200 w-56 inline-block">
                    Reg No:
                  </span>
                  <span className="text-white">
                    {response?.data?.regNo || "N/A"}
                  </span>
                </div>

                <div>
                  <span className="font-semibold text-blue-200 w-56 inline-block">
                    Registration Date:
                  </span>
                  <span className="text-white">
                    {response?.data?.regDate || "N/A"}
                  </span>
                </div>

                <div>
                  <span className="font-semibold text-blue-200 w-56 inline-block">
                    Fitness Upto:
                  </span>
                  <span className="text-white">
                    {response?.data?.fitnessUpto || "N/A"}
                  </span>
                </div>

                <div>
                  <span className="font-semibold text-blue-200 w-56 inline-block">
                    Owner SR No:
                  </span>
                  <span className="text-white">
                    {response?.data?.ownerSrNo || "N/A"}
                  </span>
                </div>

                <div>
                  <span className="font-semibold text-blue-200 w-56 inline-block">
                    Chassis No:
                  </span>
                  <span className="text-white">
                    {response?.data?.chassisNo || "N/A"}
                  </span>
                </div>

                <div>
                  <span className="font-semibold text-blue-200 w-56 inline-block">
                    Engine No:
                  </span>
                  <span className="text-white">
                    {response?.data?.engineNo || "N/A"}
                  </span>
                </div>

                <div>
                  <span className="font-semibold text-blue-200 w-56 inline-block">
                    Owner Name:
                  </span>
                  <span className="text-white">
                    {response?.data?.ownerName || "N/A"}
                  </span>
                </div>

                <div>
                  <span className="font-semibold text-blue-200 w-56 inline-block">
                    Father Name:
                  </span>
                  <span className="text-white">
                    {response?.data?.fatherName || "N/A"}
                  </span>
                </div>

                <div>
                  <span className="font-semibold text-blue-200 w-56 inline-block">
                    Fuel Type:
                  </span>
                  <span className="text-white">
                    {response?.data?.fuelType || "N/A"}
                  </span>
                </div>

                <div>
                  <span className="font-semibold text-blue-200 w-56 inline-block">
                    Fuel Norms:
                  </span>
                  <span className="text-white">
                    {response?.data?.fuelNorms || "N/A"}
                  </span>
                </div>

                <div>
                  <span className="font-semibold text-blue-200 w-56 inline-block">
                    Permanent Address:
                  </span>
                  <span className="text-white">
                    {response?.data?.permanentAddress || "N/A"}
                  </span>
                </div>

                <div>
                  <span className="font-semibold text-blue-200 w-56 inline-block">
                    Present Address:
                  </span>
                  <span className="text-white">
                    {response?.data?.presentAddress || "N/A"}
                  </span>
                </div>

                <div>
                  <span className="font-semibold text-blue-200 w-56 inline-block">
                    Vehicle Category:
                  </span>
                  <span className="text-white">
                    {response?.data?.vehicleCategory || "N/A"}
                  </span>
                </div>

                <div>
                  <span className="font-semibold text-blue-200 w-56 inline-block">
                    Maker:
                  </span>
                  <span className="text-white">
                    {response?.data?.maker || "N/A"}
                  </span>
                </div>

                <div>
                  <span className="font-semibold text-blue-200 w-56 inline-block">
                    Maker Model:
                  </span>
                  <span className="text-white">
                    {response?.data?.makerModel || "N/A"}
                  </span>
                </div>

                <div>
                  <span className="font-semibold text-blue-200 w-56 inline-block">
                    Vehicle Color:
                  </span>
                  <span className="text-white">
                    {response?.data?.vehicleColor || "N/A"}
                  </span>
                </div>

                <div>
                  <span className="font-semibold text-blue-200 w-56 inline-block">
                    Body Type:
                  </span>
                  <span className="text-white">
                    {response?.data?.bodyTypeDes || "N/A"}
                  </span>
                </div>

                <div>
                  <span className="font-semibold text-blue-200 w-56 inline-block">
                    Manufactured Month/Year:
                  </span>
                  <span className="text-white">
                    {response?.data?.manufacturedMonthYear || "N/A"}
                  </span>
                </div>

                <div>
                  <span className="font-semibold text-blue-200 w-56 inline-block">
                    Unladen Weight:
                  </span>
                  <span className="text-white">
                    {response?.data?.unladenWeight || "N/A"}
                  </span>
                </div>

                <div>
                  <span className="font-semibold text-blue-200 w-56 inline-block">
                    Vehicle Gross Weight:
                  </span>
                  <span className="text-white">
                    {response?.data?.vehicleGrossWeight || "N/A"}
                  </span>
                </div>

                <div>
                  <span className="font-semibold text-blue-200 w-56 inline-block">
                    No of Cylinders:
                  </span>
                  <span className="text-white">
                    {response?.data?.noCylinders || "N/A"}
                  </span>
                </div>

                <div>
                  <span className="font-semibold text-blue-200 w-56 inline-block">
                    Cubic Capacity:
                  </span>
                  <span className="text-white">
                    {response?.data?.cubicCap || "N/A"}
                  </span>
                </div>

                <div>
                  <span className="font-semibold text-blue-200 w-56 inline-block">
                    Wheel Base:
                  </span>
                  <span className="text-white">
                    {response?.data?.wheelBase || "N/A"}
                  </span>
                </div>

                <div>
                  <span className="font-semibold text-blue-200 w-56 inline-block">
                    No of Seats:
                  </span>
                  <span className="text-white">
                    {response?.data?.noOfSeats || "N/A"}
                  </span>
                </div>

                <div>
                  <span className="font-semibold text-blue-200 w-56 inline-block">
                    Financer Details:
                  </span>
                  <span className="text-white">
                    {response?.data?.financerDetails || "N/A"}
                  </span>
                </div>

                <div>
                  <span className="font-semibold text-blue-200 w-56 inline-block">
                    RTO:
                  </span>
                  <span className="text-white">
                    {response?.data?.rto || "N/A"}
                  </span>
                </div>

                <div>
                  <span className="font-semibold text-blue-200 w-56 inline-block">
                    IDS Response:
                  </span>
                  <span className="text-white">
                    {response?.data?.ids_response || "N/A"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default RcCardDetails;
